import React, { useState, useEffect } from "react";
import {
  ImagePlus,
  Palette,
  Globe,
  FileText,
  Users,
  Mail,
  Bell,
  Check,
  Sparkles,
  Clock,
  X,
} from "lucide-react";
import api from "../api/client";

const PREVIEW_FEATURES = [
  {
    icon: ImagePlus,
    title: "Custom Logo Upload",
    description: "Replace OnesuiteSEO branding with yours",
    color: "text-violet-400",
    bg: "bg-violet-500/10",
  },
  {
    icon: Palette,
    title: "Brand Colors",
    description: "Match your agency's color palette",
    color: "text-pink-400",
    bg: "bg-pink-500/10",
  },
  {
    icon: Globe,
    title: "Custom Domain",
    description: "seo.youragency.com",
    color: "text-sky-400",
    bg: "bg-sky-500/10",
  },
  {
    icon: FileText,
    title: "Branded PDF Reports",
    description: "Send client reports with your logo",
    color: "text-amber-400",
    bg: "bg-amber-500/10",
  },
  {
    icon: Users,
    title: "Client Sub-Accounts",
    description: "Give clients their own login",
    color: "text-emerald-400",
    bg: "bg-emerald-500/10",
  },
  {
    icon: Mail,
    title: "Custom Email From",
    description: "Notifications from your domain",
    color: "text-orange-400",
    bg: "bg-orange-500/10",
  },
];

export default function WhiteLabel() {
  const [registered, setRegistered] = useState(false);
  const [loading, setLoading] = useState(false);
  const [interestCount, setInterestCount] = useState(0);
  const [showBanner, setShowBanner] = useState(false);
  const [justRegistered, setJustRegistered] = useState(false);

  useEffect(() => {
    api
      .get("/white-label/interest-count/")
      .then((res) => {
        setInterestCount(res.data.count);
        setRegistered(res.data.user_registered);
        // Show banner on first visit if not registered
        const bannerKey = "wl_banner_dismissed";
        if (!res.data.user_registered && !sessionStorage.getItem(bannerKey)) {
          setShowBanner(true);
        }
      })
      .catch(() => {});
  }, []);

  const handleRegister = async () => {
    setLoading(true);
    try {
      await api.post("/white-label/register-interest/");
      setRegistered(true);
      setJustRegistered(true);
      setInterestCount((c) => c + 1);
      setShowBanner(false);
      setTimeout(() => setJustRegistered(false), 3000);
    } catch {
      // already registered
      setRegistered(true);
    } finally {
      setLoading(false);
    }
  };

  const dismissBanner = () => {
    setShowBanner(false);
    sessionStorage.setItem("wl_banner_dismissed", "1");
  };

  return (
    <div className="max-w-5xl mx-auto px-4 py-8 space-y-8">
      {/* First-visit banner */}
      {showBanner && (
        <div className="bg-gradient-to-r from-copper/20 via-amber-900/20 to-copper/10 border border-copper/30 rounded-xl p-4 flex items-center gap-4 animate-fade-in">
          <div className="w-10 h-10 bg-copper/20 rounded-lg flex items-center justify-center shrink-0">
            <Sparkles size={20} className="text-copper" />
          </div>
          <div className="flex-1 min-w-0">
            <p className="text-sm text-white font-medium">
              White label customization is coming soon
            </p>
            <p className="text-xs text-gray-400 mt-0.5">
              Click below to join the waitlist and be first to know when it launches.
            </p>
          </div>
          <button
            onClick={handleRegister}
            className="px-4 py-1.5 bg-copper hover:bg-copper-600 text-white text-xs font-semibold rounded-lg transition-colors shrink-0"
          >
            Join Waitlist
          </button>
          <button
            onClick={dismissBanner}
            className="text-gray-500 hover:text-gray-300 shrink-0"
          >
            <X size={16} />
          </button>
        </div>
      )}

      {/* Hero */}
      <div className="text-center space-y-4 py-4">
        <div className="inline-flex items-center gap-2 px-3 py-1 bg-copper/10 border border-copper/20 rounded-full text-xs text-copper font-semibold tracking-wide uppercase">
          <Clock size={12} />
          Coming Q2 2026
        </div>
        <h1 className="text-3xl md:text-4xl font-bold text-white tracking-tight">
          Your Brand, Your Platform
        </h1>
        <p className="text-gray-400 text-base max-w-xl mx-auto leading-relaxed">
          White label features are in development and will be available to Agency
          tier subscribers. Preview what's coming:
        </p>
      </div>

      {/* Feature Cards Grid */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
        {PREVIEW_FEATURES.map(({ icon: Icon, title, description, color, bg }) => (
          <div
            key={title}
            className="group relative bg-dark-300 border border-dark-50 rounded-xl p-5 space-y-3 overflow-hidden transition-all hover:border-dark-50/80"
          >
            {/* Coming Soon badge */}
            <div className="absolute top-3 right-3">
              <span className="text-[9px] font-bold px-2 py-0.5 rounded-full bg-gray-700/60 text-gray-400 uppercase tracking-wider">
                Soon
              </span>
            </div>

            {/* Icon */}
            <div className={`w-10 h-10 ${bg} rounded-lg flex items-center justify-center`}>
              <Icon size={20} className={`${color} opacity-60`} />
            </div>

            {/* Title + description */}
            <div>
              <h3 className="text-sm font-semibold text-gray-300">{title}</h3>
              <p className="text-xs text-gray-500 mt-1 leading-relaxed">
                {description}
              </p>
            </div>

            {/* Grayed overlay effect */}
            <div className="absolute inset-0 bg-dark-300/30 pointer-events-none" />
          </div>
        ))}
      </div>

      {/* Mockup preview */}
      <div className="bg-dark-300 border border-dark-50 rounded-xl p-6 space-y-4">
        <h3 className="text-sm font-semibold text-gray-300 flex items-center gap-2">
          <Sparkles size={14} className="text-copper" />
          Live Preview (Mockup)
        </h3>
        <div className="bg-dark rounded-lg border border-dark-50 p-4 space-y-3">
          {/* Fake branded header */}
          <div className="flex items-center gap-3 pb-3 border-b border-dark-50">
            <div className="w-8 h-8 rounded-lg bg-gradient-to-br from-violet-500 to-pink-500 flex items-center justify-center">
              <span className="text-white text-xs font-bold">YB</span>
            </div>
            <div>
              <div className="text-sm font-bold text-white">YourBrand SEO</div>
              <div className="text-[10px] text-gray-500">
                seo.yourbrand.com
              </div>
            </div>
            <div className="ml-auto flex gap-1">
              <div className="w-2 h-2 rounded-full bg-violet-500" />
              <div className="w-2 h-2 rounded-full bg-pink-500" />
              <div className="w-2 h-2 rounded-full bg-gray-600" />
            </div>
          </div>
          {/* Fake nav items */}
          <div className="flex gap-2">
            {["Dashboard", "Keywords", "Content", "Reports"].map((n) => (
              <div
                key={n}
                className={`text-[10px] px-2.5 py-1 rounded ${
                  n === "Dashboard"
                    ? "bg-violet-500/15 text-violet-400"
                    : "text-gray-500"
                }`}
              >
                {n}
              </div>
            ))}
          </div>
          {/* Fake content area */}
          <div className="grid grid-cols-3 gap-2">
            {[1, 2, 3].map((i) => (
              <div key={i} className="bg-dark-50/50 rounded-lg p-3 space-y-2">
                <div className="w-8 h-1.5 bg-gray-700 rounded" />
                <div className="w-12 h-3 bg-gray-700/50 rounded" />
              </div>
            ))}
          </div>
        </div>
        <p className="text-[11px] text-gray-600 text-center">
          This is a visual mockup. Actual customization options will vary.
        </p>
      </div>

      {/* CTA Section */}
      <div className="text-center space-y-4 py-6">
        {registered ? (
          <div className="inline-flex items-center gap-2 px-6 py-3 bg-emerald-500/10 border border-emerald-500/20 rounded-xl">
            <Check size={18} className="text-emerald-400" />
            <span className="text-emerald-300 text-sm font-medium">
              {justRegistered
                ? "You're on the waitlist!"
                : "You're already on the waitlist"}
            </span>
          </div>
        ) : (
          <button
            onClick={handleRegister}
            disabled={loading}
            className="inline-flex items-center gap-2 px-8 py-3 bg-copper hover:bg-copper-600 text-white text-sm font-semibold rounded-xl transition-all disabled:opacity-50 shadow-lg shadow-copper/20"
          >
            <Bell size={16} />
            {loading ? "Joining..." : "Notify Me When Available"}
          </button>
        )}
        {interestCount > 0 && (
          <p className="text-xs text-gray-500">
            {interestCount} {interestCount === 1 ? "person has" : "people have"}{" "}
            joined the waitlist
          </p>
        )}
      </div>
    </div>
  );
}
